import React from 'react';
import * as ThemeUI from 'theme-ui';
import { WidgetProps } from '@rjsf/core';
import { FormattedMessage } from 'lib/intl';
import { OutlinedTextWidget } from 'components/form-elements/JsonForms/components/Text';
import { ErrorMessage } from 'components/form-elements/JsonForms/components/ErrorMessage';
import { LocaleKey } from 'translations';

/** Inclusive lower bound for the "Length of pipe" field (metres). */
export const LENGTH_OF_PIPE_MIN = 0.1;

/** Inclusive upper bound for the "Length of pipe" field (metres). */
export const LENGTH_OF_PIPE_MAX = 1000;

/** Translation key for the "out of range" validation message. */
export const LENGTH_OF_PIPE_RANGE_ERROR: LocaleKey = 'errors.lengthOfPipe.range';

/**
 * Whether a "Length of pipe" value falls outside the allowed
 * {@link LENGTH_OF_PIPE_MIN}-{@link LENGTH_OF_PIPE_MAX} range.
 *
 * Empty values are treated as valid (the calculator simply does not compute
 * until every input is filled in), and non-numeric text is left to the schema
 * type validation, so only a real, in-range-violating number is flagged here.
 */
export function isLengthOfPipeOutOfRange(value?: unknown): boolean {
  if (value === undefined || value === null || value === '') {
    return false;
  }

  const num = typeof value === 'number' ? value : Number(value);
  if (Number.isNaN(num)) {
    return false;
  }

  return num < LENGTH_OF_PIPE_MIN || num > LENGTH_OF_PIPE_MAX;
}

/**
 * Widget for the calculator's "Length of pipe" (`lengthOfPipe`) number field.
 *
 * Behaves like the regular number text widget but adds an instant
 * (per-keystroke) validation that keeps the value within
 * {@link LENGTH_OF_PIPE_MIN}-{@link LENGTH_OF_PIPE_MAX} metres. As soon as the
 * user types a negative / zero / too-large value the input is marked as errored
 * and an inline message is shown - the user does not have to submit first.
 *
 * Submission is additionally blocked by the form-level `validate` callback in
 * `ReliningPage`, which surfaces the same error (via `FieldTemplate`) after a
 * submit attempt. To avoid showing the message twice, the inline message here
 * is suppressed once that form error is already present.
 *
 * Mirrors the "Type of Equipment Used" (`EquipmentTypeWidget`) validation.
 */
export function LengthOfPipeWidget(props: WidgetProps) {
  const outOfRange = isLengthOfPipeOutOfRange(props.value);
  const alreadyReported = (props.rawErrors ?? []).includes(
    LENGTH_OF_PIPE_RANGE_ERROR,
  );

  /*
   * Feed the range error into the underlying input so it is styled as errored
   * immediately, without waiting for a submit attempt to turn on live
   * validation.
   */
  const rawErrors = outOfRange && !alreadyReported
    ? [...(props.rawErrors ?? []), LENGTH_OF_PIPE_RANGE_ERROR]
    : props.rawErrors;

  return (
    <React.Fragment>
      <OutlinedTextWidget {...props} rawErrors={rawErrors} />

      {outOfRange && !alreadyReported && (
        <ThemeUI.Box mt={1}>
          <ErrorMessage>
            <FormattedMessage id={LENGTH_OF_PIPE_RANGE_ERROR} />
          </ErrorMessage>
        </ThemeUI.Box>
      )}
    </React.Fragment>
  );
}
